import React, { PropsWithChildren, ReactElement, useState } from "react";
import {
    useFloating,
    autoUpdate,
    offset,
    flip,
    shift,
    useDismiss,
    useRole,
    useClick,
    useInteractions,
    FloatingFocusManager,
    FloatingPortal,
    Placement,
} from "@floating-ui/react";
import useBreakpoint, {
    BreakpointComparer,
} from "utilities/hooks/use-breakpoint";
import { Breakpoints } from "utilities/enumerations/breakpoints";
import useModalTransition from "utilities/hooks/use-modal-transition";
import Modal from "molecules/modals/modal";
import { ModalTypes } from "molecules/constants/modal-types";

const CSS_CLASS_NAME = "c-floating-menu-button";
interface FloatingMenuButtonProps extends PropsWithChildren {
    trigger: ReactElement;
    placement?: Placement;
}
const FloatingMenuButton = ({
    trigger,
    children,
    placement = "bottom-start",
}: FloatingMenuButtonProps) => {
    const [isOpen, setIsOpen] = useState(false);

    const isMobile = useBreakpoint(
        Breakpoints.Phone,
        BreakpointComparer.MaxWidth
    );
    const transitionEffect = useModalTransition();

    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
        placement,
        middleware: [
            offset(8),
            flip({ fallbackAxisSideDirection: "end" }),
            shift({ padding: 8 }),
        ],
        whileElementsMounted: autoUpdate,
    });

    const click = useClick(context);
    const dismiss = useDismiss(context);
    const role = useRole(context);

    const { getReferenceProps, getFloatingProps } = useInteractions([
        click,
        dismiss,
        role,
    ]);

    const renderChildren = () => {
        if (!isOpen) {
            return;
        }

        return React.Children.map(
            children,
            (child: React.ReactNode, index: number) => {
                if (!React.isValidElement(child)) {
                    return child;
                }

                return (
                    <span key={index} className={`${CSS_CLASS_NAME}__item`}>
                        {React.cloneElement(child, {
                            ...child.props,
                            onClick: (e: React.MouseEvent) => {
                                // Retain default functionality if there's a link/anchor prop
                                if (child.props.to == null) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }

                                setIsOpen(false);

                                if (child.props.onClick != null) {
                                    child.props.onClick();
                                }
                            },
                        })}
                    </span>
                );
            }
        );
    };

    return (
        <>
            {React.cloneElement(trigger, {
                ref: refs.setReference,
                ...getReferenceProps(trigger.props),
            })}

            {isOpen &&
                (isMobile ? (
                    <Modal
                        closeDialog={() => setIsOpen(false)}
                        isVisible={isOpen}
                        label="Change Edition"
                        transition={transitionEffect}
                        type={ModalTypes.Bottom}>
                        {renderChildren()}
                    </Modal>
                ) : (
                    <FloatingPortal>
                        <FloatingFocusManager context={context} modal={false}>
                            <div
                                className={`${CSS_CLASS_NAME}__list`}
                                ref={refs.setFloating}
                                style={{
                                    ...floatingStyles,
                                }}
                                {...getFloatingProps()}>
                                {renderChildren()}
                            </div>
                        </FloatingFocusManager>
                    </FloatingPortal>
                ))}
        </>
    );
};

export default FloatingMenuButton;
